import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Assessment as SummaryIcon
} from '@mui/icons-material';
import { apiFetch } from '../../utils/api';
import { Logger } from '../../utils/logger';
import { BRAND_PURPLE, COLOR_GOOD, COLOR_WARN, COLOR_POOR, BG_SURFACE, BG_ELEVATED } from '../../utils/colors';
import { generateExecutiveSummaryPDF } from '../../utils/generateDocumentPDF';

const logger = new Logger('ExecutiveSummaryModal');

const BulletList = ({ items, color }) => {
  if (!items?.length) return null;
  return (
    <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
      {items.map((item, i) => (
        <Typography key={i} component="li" variant="body2" sx={{ color: color || '#e0e0e0', lineHeight: 1.7 }}>
          {item}
        </Typography>
      ))}
    </Box>
  );
};

const SectionCard = ({ title, children }) => (
  <Box sx={{ p: 2, bgcolor: BG_ELEVATED, border: `1px solid ${BRAND_PURPLE}26`, borderRadius: 2, mb: 2 }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: BRAND_PURPLE, mb: 1 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const ExecutiveSummaryModal = ({
  open,
  onClose,
  configurationId,
  configurationName,
  projectName,
  aiAnalysis = null,
  savedSummary = null,
  comparisonData = null,
  opportunityMeta = {}
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [summary, setSummary] = useState(savedSummary);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [error, setError] = useState(null);

  // Load saved summary when modal opens
  useEffect(() => {
    if (open && savedSummary && !summary) {
      setSummary(savedSummary);
    }
  }, [open, savedSummary]);

  const handleGenerate = async (forceRefresh = false) => {
    if (!configurationId) return;
    setIsGenerating(true);
    setError(null);
    if (forceRefresh) setSummary(null);

    try {
      const response = await apiFetch('team-optimization/compare/executive-summary', {
        method: 'POST',
        body: JSON.stringify({
          configuration_id: configurationId,
          ai_analysis: aiAnalysis,
          force_refresh: forceRefresh
        })
      });

      if (!response.ok) throw new Error(`HTTP error ${response.status}`);
      const data = await response.json();

      if (data.status === 'success') {
        setSummary(data.executive_summary);
        setGeneratedAt(new Date().toISOString());
      } else {
        throw new Error(data.error || 'Failed to generate executive summary');
      }
    } catch (err) {
      logger.error('Error generating executive summary:', err);
      setError(err.message || 'Failed to generate executive summary');
    } finally {
      setIsGenerating(false);
    }
  };

  // Auto-load when opened if no cached summary exists
  useEffect(() => {
    if (open && !summary && !isGenerating) {
      handleGenerate(false);
    }
  }, [open]);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const fin = summary?.financial_summary || {};
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { bgcolor: BG_SURFACE, color: 'white', maxHeight: '90vh' }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        bgcolor: BG_ELEVATED,
        borderBottom: `1px solid ${BRAND_PURPLE}26`,
        py: 1.5
      }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.3 }}>
            <SummaryIcon sx={{ mr: 1, color: BRAND_PURPLE, verticalAlign: 'middle', fontSize: '1.2rem' }} />
            Executive Summary
          </Typography>
          {(configurationName || projectName) && (
            <Typography variant="caption" sx={{ color: '#aaa', display: 'block', mt: 0.25 }}>
              {configurationName || projectName}
              {generatedAt ? ` · Generated ${new Date(generatedAt).toLocaleString()}` : ''}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {summary && !isGenerating && (
            <Button
              startIcon={<RefreshIcon />}
              onClick={() => handleGenerate(true)}
              variant="outlined"
              size="small"
              sx={{ borderColor: BRAND_PURPLE, color: BRAND_PURPLE, '&:hover': { borderColor: BRAND_PURPLE, bgcolor: `${BRAND_PURPLE}0D` } }}
            >
              Regenerate
            </Button>
          )}
          {summary && (
            <Button
              startIcon={<DownloadIcon />}
              onClick={() => generateExecutiveSummaryPDF(summary, configurationName, projectName, comparisonData, opportunityMeta)}
              variant="outlined"
              size="small"
              sx={{ borderColor: BRAND_PURPLE, color: BRAND_PURPLE, '&:hover': { borderColor: BRAND_PURPLE, bgcolor: `${BRAND_PURPLE}0D` } }}
            >
              Download PDF
            </Button>
          )}
          <IconButton onClick={handleClose} sx={{ color: 'white' }} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3, overflow: 'auto' }}>
        {isGenerating && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 2 }}>
            <CircularProgress size={40} sx={{ color: BRAND_PURPLE }} />
            <Typography variant="body2" color="text.secondary">Generating executive summary...</Typography>
          </Box>
        )}

        {error && !isGenerating && (
          <Alert severity="error" sx={{ mb: 2, bgcolor: `${COLOR_POOR}15`, color: COLOR_POOR }}>
            {error}
          </Alert>
        )}

        {!isGenerating && summary && (
          <Box>
            {/* Recommendation */}
            {summary.recommendation && (
              <Box sx={{
                p: 2.5, mb: 2,
                background: `linear-gradient(135deg, ${BRAND_PURPLE}1A 0%, ${BRAND_PURPLE}07 100%)`,
                border: `1px solid ${BRAND_PURPLE}4D`, borderRadius: 2
              }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: BRAND_PURPLE, mb: 1 }}>
                  Executive Recommendation
                </Typography>
                <Typography variant="body2" sx={{ color: '#e0e0e0', lineHeight: 1.7 }}>
                  {summary.recommendation}
                </Typography>
              </Box>
            )}

            {/* Two-column layout */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
              {summary.team_overview?.length > 0 && (
                <SectionCard title="Team Overview">
                  <BulletList items={summary.team_overview} />
                </SectionCard>
              )}

              {(fin.total_cost || fin.margin) && (
                <SectionCard title="Financial Summary">
                  {fin.total_cost && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" sx={{ color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Estimated Cost
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
                        {fin.total_cost}
                      </Typography>
                    </Box>
                  )}
                  {fin.margin && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" sx={{ color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Margin
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: COLOR_GOOD }}>
                        {fin.margin}
                      </Typography>
                    </Box>
                  )}
                  {fin.value_statement && (
                    <Typography variant="body2" sx={{ color: '#ccc', lineHeight: 1.6, mt: 1 }}>
                      {fin.value_statement}
                    </Typography>
                  )}
                </SectionCard>
              )}
            </Box>

            {summary.success_factors?.length > 0 && (
              <SectionCard title="Success Factors">
                <BulletList items={summary.success_factors} color={COLOR_GOOD} />
              </SectionCard>
            )}

            {summary.timeline && (summary.timeline.duration || summary.timeline.readiness) && (
              <SectionCard title="Project Timeline">
                {summary.timeline.duration && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ color: '#888' }}>Duration</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: 'white' }}>
                      {summary.timeline.duration}
                    </Typography>
                  </Box>
                )}
                {summary.timeline.readiness && (
                  <Typography variant="body2" sx={{ color: '#ccc', lineHeight: 1.6 }}>
                    {summary.timeline.readiness}
                  </Typography>
                )}
              </SectionCard>
            )}

            {summary.considerations?.filter(c => c).length > 0 && (
              <SectionCard title="Considerations">
                <BulletList items={summary.considerations.filter(c => c)} color={COLOR_WARN} />
              </SectionCard>
            )}
          </Box>
        )}

        {!isGenerating && !summary && !error && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="body2" sx={{ color: '#9e9e9e', mb: 2 }}>
              Generate a professional, client-ready executive summary for this lineup recommendation.
            </Typography>
            <Button
              variant="contained"
              startIcon={<SummaryIcon />}
              onClick={() => handleGenerate(false)}
              sx={{ bgcolor: BRAND_PURPLE, '&:hover': { bgcolor: BRAND_PURPLE } }}
            >
              Generate Summary
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ExecutiveSummaryModal;
